import { useEffect, useState } from 'react';
import { X, ArchiveRestore, Trash2, Layout } from 'lucide-react';
import useBoardStore from '../../store/boardStore';
import formatRelative from '../../utils/formatRelative';
import { dialog } from '../../store/dialogStore';

const TABS = [
  { id: 'cards', label: 'Itens' },
  { id: 'groups', label: 'Grupos' },
  { id: 'boards', label: 'Quadros' },
];

export default function ArchivedModal({ onClose }) {
  const {
    currentBoard,
    fetchArchivedCards, unarchiveCard, deleteCard,
    fetchArchivedGroups, restoreGroup, deleteGroup,
    fetchArchivedBoards, restoreBoard, deleteBoard,
    fetchBoards,
  } = useBoardStore();

  const [tab, setTab] = useState('cards');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!currentBoard) return;
    setLoading(true);
    try {
      if (tab === 'cards') setItems(await fetchArchivedCards(currentBoard.id));
      else if (tab === 'groups') setItems(await fetchArchivedGroups(currentBoard.id));
      else if (tab === 'boards') setItems(await fetchArchivedBoards(currentBoard.workspace_id));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab, currentBoard?.id]);

  const handleRestoreCard = async (id) => {
    await unarchiveCard(id);
    setItems((prev) => prev.filter((c) => c.id !== id));
  };
  const handleDeleteCard = async (id) => {
    if (!(await dialog.confirm('Excluir permanentemente', 'Esta ação não pode ser desfeita.', { confirmLabel: 'Excluir', danger: true }))) return;
    await deleteCard(id);
    setItems((prev) => prev.filter((c) => c.id !== id));
  };

  const handleRestoreGroup = async (id) => {
    await restoreGroup(id);
    setItems((prev) => prev.filter((g) => g.id !== id));
  };
  const handleDeleteGroup = async (id) => {
    if (!(await dialog.confirm('Excluir grupo', 'O grupo e todos os seus itens serão excluídos permanentemente.', { confirmLabel: 'Excluir', danger: true }))) return;
    await deleteGroup(id);
    setItems((prev) => prev.filter((g) => g.id !== id));
  };

  const handleRestoreBoard = async (id) => {
    await restoreBoard(id);
    if (fetchBoards && currentBoard?.workspace_id) fetchBoards(currentBoard.workspace_id);
    setItems((prev) => prev.filter((b) => b.id !== id));
  };
  const handleDeleteBoard = async (id) => {
    if (!(await dialog.confirm('Excluir quadro', 'O quadro e todo o seu conteúdo serão excluídos permanentemente.', { confirmLabel: 'Excluir', danger: true }))) return;
    await deleteBoard(id);
    setItems((prev) => prev.filter((b) => b.id !== id));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[560px] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <h3 className="text-sm font-semibold text-text-primary">Lixeira</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex border-b border-border/50 px-5">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8 text-sm text-text-secondary">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-sm text-text-secondary">Lixeira vazia.</div>
          ) : (
            <div className="space-y-2">
              {tab === 'cards' && items.map((c) => (
                <div key={c.id} className="flex items-center gap-3 bg-main border border-border/50 rounded-lg p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary truncate">{c.title}</div>
                    <div className="text-[11px] text-text-secondary">Arquivado {formatRelative(c.archived_at)}</div>
                  </div>
                  <button
                    onClick={() => handleRestoreCard(c.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-accent hover:bg-accent/10 rounded transition-colors shrink-0"
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" />
                    Restaurar
                  </button>
                  <button
                    onClick={() => handleDeleteCard(c.id)}
                    className="p-1.5 text-text-secondary hover:text-danger hover:bg-white/5 rounded transition-colors shrink-0"
                    title="Excluir permanentemente"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {tab === 'groups' && items.map((g) => (
                <div key={g.id} className="flex items-center gap-3 bg-main border border-border/50 rounded-lg p-3">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary truncate">{g.name}</div>
                    <div className="text-[11px] text-text-secondary">
                      {g.card_count} {Number(g.card_count) === 1 ? 'item' : 'itens'} · Arquivado {formatRelative(g.archived_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRestoreGroup(g.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-accent hover:bg-accent/10 rounded transition-colors shrink-0"
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" />
                    Restaurar
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(g.id)}
                    className="p-1.5 text-text-secondary hover:text-danger hover:bg-white/5 rounded transition-colors shrink-0"
                    title="Excluir permanentemente"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {tab === 'boards' && items.map((b) => (
                <div key={b.id} className="flex items-center gap-3 bg-main border border-border/50 rounded-lg p-3">
                  <Layout className="w-4 h-4 text-text-secondary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary truncate">{b.name}</div>
                    <div className="text-[11px] text-text-secondary">Arquivado {formatRelative(b.archived_at)}</div>
                  </div>
                  <button
                    onClick={() => handleRestoreBoard(b.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-accent hover:bg-accent/10 rounded transition-colors shrink-0"
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" />
                    Restaurar
                  </button>
                  <button
                    onClick={() => handleDeleteBoard(b.id)}
                    className="p-1.5 text-text-secondary hover:text-danger hover:bg-white/5 rounded transition-colors shrink-0"
                    title="Excluir permanentemente"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
